import { StaticImage } from 'gatsby-plugin-image';
import * as React from 'react';

const imgClassName = 'rounded-xl shadow-xl ring-1 ring-black ring-opacity-5';

const classroomHeroSeatedImg = (
  <StaticImage
    src="../../images/landing/print-examples/hero-seated-04-24.jpg"
    alt="A seating chart with a classroom layout and students seated. The desks are arranged in clusters of four, one round table of six, and seven separate seats. The classroom has a wooden floor, a decorative plant, and a whiteboard at the front."
    className={imgClassName}
    width={512}
  />
);

const classroomLectureHallImg = (
  <StaticImage
    src="../../images/landing/print-examples/lecture-hall-04-24.jpg"
    alt="Printed seating chart with lecture hall layout and students seated. The back row is empty. The room layout is minimalistic without floor texture or decorations. The seating plan has labels for row numbers."
    className={imgClassName}
    width={512}
  />
);

const classroomSmallGridImg = (
  <StaticImage
    src="../../images/landing/print-examples/grid-04-24.jpg"
    alt="Printed seating chart with a grid layout and students seated. The room has a tiled, light coloured floor and a whiteboard at the front. In addition there are windows, a door and a sink. The room has in total 20 desks, the front corner ones being slightly rotated."
    className={imgClassName}
    width={512}
  />
);

const weddingRoundImg = (
  <StaticImage
    src="../../images/landing/print-examples/round-event-04-24.jpg"
    alt="Printed seating chart with round tables for a small wedding reception or other event. The room has a tiled floor, plants and a buffet table. The guest cards are simple with black text on a white background."
    className={imgClassName}
    width={512}
  />
);

const weddingLongMImg = (
  <StaticImage
    src="../../images/landing/print-examples/event-medium-long-04-24.jpg"
    alt="Printed seating chart with three long tables. The room has a white floor and some decorative objects to help visualize the layout. The guest cards have the guest's name and portrait or initials on a light blue background."
    className={imgClassName}
    width={512}
  />
);

const weddingLongLImg = (
  <StaticImage
    src="../../images/landing/print-examples/event-lg-04-24.jpg"
    alt="Printed seating chart with extra long tables for a large wedding reception or other event with up to 100 guests. The room has a wooden floor. The guest cards have the guest's name and an avatar generated from their initials on a white background. About half of the seats are occupied."
    className={imgClassName}
    width={512}
  />
);

const contentDefault = {
  heading: <>Examples of seating charts made with the app</>,
  description: (
    <>
      Save your seating charts in the app, export them to PDF, or print them on
      paper for easy keeping or sharing.
    </>
  ),
  images: [weddingLongLImg, classroomLectureHallImg, classroomSmallGridImg],
};

const contentClassroom = {
  heading: <>Examples of seating charts made with the app</>,
  description: (
    <>
      Save your seating charts in the app, export them to PDF, or print them on
      paper for easy keeping or sharing.
    </>
  ),
  images: [
    classroomHeroSeatedImg,
    classroomLectureHallImg,
    classroomSmallGridImg,
  ],
};

const contentWedding = {
  heading: <>Examples of seating charts made with the app</>,
  description: (
    <>
      Save your seating charts in the app, export them to PDF, or print them on
      paper for easy keeping or sharing.
    </>
  ),
  images: [weddingRoundImg, weddingLongMImg, weddingLongLImg],
};

type Content = typeof contentDefault;

const PrintExamples: React.FC<{ content: Content }> = ({ content }) => (
  <div className="relative bg-gray-200 pt-16 pb-20 px-4 sm:px-6 lg:pt-24 lg:pb-28 lg:px-8">
    <div className="absolute inset-0">
      <div className="bg-white h-1/3 sm:h-2/3" />
    </div>
    <div className="relative max-w-7xl mx-auto">
      <div className="text-center">
        <h2 className="tracking-tight font-extrabold text-gray-900 text-3xl sm:text-4xl">
          {content.heading}
        </h2>
        <p className="mt-3 max-w-2xl mx-auto text-xl text-gray-600 sm:mt-4">
          {content.description}
        </p>
      </div>
      <div className="mt-12 max-w-lg mx-auto grid gap-5 lg:grid-cols-3 lg:max-w-none">
        {content.images.map((image, index) => (
          <React.Fragment key={index}>{image}</React.Fragment>
        ))}
      </div>
    </div>
  </div>
);

export const PrintExamplesDefault = () => (
  <PrintExamples content={contentDefault} />
);

export const PrintExamplesClassroom = () => (
  <PrintExamples content={contentClassroom} />
);

export const PrintExamplesWedding = () => (
  <PrintExamples content={contentWedding} />
);
